/**
 * DeleteWorkspaceDialog Component
 * 
 * Dialog for confirming deletion of a workspace (owners only).
 */

import { Typography, Box, TextField } from "@mui/material";
import StandardDialog from "../../../components/StandardDialog";
import { Workspace } from "../../../types";

export interface DeleteWorkspaceDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  workspace: Workspace;
  deleteWorkspaceName: string;
  setDeleteWorkspaceName: (name: string) => void;
  deleting: boolean;
}

export default function DeleteWorkspaceDialog({
  open,
  onClose,
  onConfirm,
  workspace,
  deleteWorkspaceName,
  setDeleteWorkspaceName,
  deleting,
}: DeleteWorkspaceDialogProps) {
  const handleClose = (): void => {
    if (!deleting) {
      setDeleteWorkspaceName("");
      onClose();
    }
  };

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title="Delete Workspace"
      confirmText={deleting ? "Deleting..." : "Delete Workspace"}
      onConfirm={onConfirm}
      confirmDisabled={deleting || deleteWorkspaceName.trim() !== workspace.name.trim()}
      confirmColor="error"
      disableClose={deleting}
      alert={{
        severity: "error",
        message: "All data will be permanently deleted. This action cannot be undone.",
      }}
    >
      <Typography sx={{ textAlign: "center", mb: 2 }}>
        Deleting this workspace will permanently delete all accounts, transactions, categories, and all associated data.
      </Typography>
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          To confirm, please type <strong>{workspace.name}</strong> in the field below:
        </Typography>
        <TextField
          fullWidth
          value={deleteWorkspaceName}
          onChange={(e) => setDeleteWorkspaceName(e.target.value)}
          placeholder="Type the workspace name to confirm"
          autoFocus
          error={deleteWorkspaceName.trim() !== "" && deleteWorkspaceName.trim() !== workspace.name.trim()}
          helperText={
            deleteWorkspaceName.trim() !== "" && deleteWorkspaceName.trim() !== workspace.name.trim()
              ? "The name does not match"
              : ""
          }
          disabled={deleting}
        />
      </Box>
    </StandardDialog>
  );
}
